import React from 'react'
import { useTranslation } from 'react-i18next'
import useCurrentSubscription from '../../../services/subscription/useCurrentSubscription'
import PlanActions from './PlanActions'
import PlanDescription from './PlanDescription'
import Spinner from '../../UI/Spinner'
import LoadFailed from '../../UI/LoadFailed'
import usePricingPlans from '../../../services/payment/usePricingPlans'
import useBillingInformation from '../../../services/subscription/useBillingInformation'

const PlanLoader = () => {
  const { t } = useTranslation()
  const {
    data: activeSubscription,
    isInitialLoading: isLoadingActiveSubscription,
    isError: isActiveSubscriptionError,
    refetch: refetchActiveSubscription,
  } = useCurrentSubscription()
  const {
    data: pricingPlans,
    isInitialLoading: isLoadingPricingPlans,
    isError: isPricingPlansError,
  } = usePricingPlans({
    enabled: !!activeSubscription,
  })

  // Deliberately outside the loading and failure gates below: the plan is
  // readable without a billing address, so a slow or failing address request
  // must not hold the card back. No address yet simply means no VAT suffix.
  const { data: billingInformation } = useBillingInformation()

  // `isInitialLoading`, not `isLoading`: a *disabled* query reports
  // isLoading:true forever in react-query v4, so gating on it left the card
  // spinning indefinitely whenever the subscription request failed.
  const isLoading = isLoadingActiveSubscription || isLoadingPricingPlans
  const hasFailed = isActiveSubscriptionError || isPricingPlansError

  return (
    <div className='shadow-form rounded-20px p-5 sm:p-10 mt-7'>
      {/* Failure wins over loading: a retrying query still reports as loading,
          which would otherwise hide the error indefinitely. */}
      {isLoading && !hasFailed && (
        <div className='flex items-center justify-center'>
          <Spinner spinner='form' color='#0C75FF' width='w-11' height='h-11' />
        </div>
      )}

      {hasFailed && (
        <LoadFailed
          message={t('failedToLoadPlan')}
          onRetry={refetchActiveSubscription}
        />
      )}

      {/* Guard on the data itself: the request can resolve with no active
          subscription, which is not an error but has nothing to render. */}
      {!isLoading && !hasFailed && !activeSubscription && (
        <p className='text-lightGray text-lg text-center py-10'>
          {t('noActiveSubscription')}
        </p>
      )}

      {!isLoading && !hasFailed && activeSubscription && (
        <>
          <PlanDescription
            activeSubscription={activeSubscription}
            pricingPlans={pricingPlans ?? []}
            billingCountry={billingInformation?.country}
          />
          <hr className='mx-auto my-6 w-full border-[#E2E2E2]' />
          <PlanActions
            activeSubscription={activeSubscription}
            pricingPlans={pricingPlans ?? []}
          />
        </>
      )}
    </div>
  )
}

export default PlanLoader
